import React, { useState } from 'react';
import { Filter, X, Calendar, Search } from 'lucide-react';

interface SalesFiltersProps {
  filters: {
    status?: 'unpaid' | 'paid' | 'void';
    paymentMethod?: 'cash' | 'debit' | 'online' | 'qris';
    startDate?: string;
    endDate?: string;
    orderBy?: string;
  };
  onFilterChange: (filters: {
    status?: 'unpaid' | 'paid' | 'void';
    paymentMethod?: 'cash' | 'debit' | 'online' | 'qris';
    startDate?: string;
    endDate?: string;
    orderBy?: string;
  }) => void;
  onClearFilters: () => void;
}

const SalesFilters: React.FC<SalesFiltersProps> = ({ 
  filters, 
  onFilterChange, 
  onClearFilters 
}) => {
  const [localStartDate, setLocalStartDate] = useState(filters.startDate || '');
  const [localEndDate, setLocalEndDate] = useState(filters.endDate || '');

  const hasActiveFilters = Object.values(filters).some(value => value !== undefined && value !== '');

  const handleFilterChange = (key: keyof typeof filters, value: string | undefined) => {
    onFilterChange({
      ...filters,
      [key]: value
    });
  };

  const handleDateChange = () => {
    onFilterChange({
      ...filters,
      startDate: localStartDate || undefined,
      endDate: localEndDate || undefined
    });
  };

  const handleFilterRemove = (key: keyof typeof filters) => {
    onFilterChange({
      ...filters,
      [key]: undefined
    });
  };

  const handleClearAll = () => {
    setLocalStartDate('');
    setLocalEndDate('');
    onClearFilters();
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Filter className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4 mr-1" />
            Clear all
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="void">Void</option>
          </select>
        </div>

        {/* Payment Method Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Method
          </label>
          <select
            value={filters.paymentMethod || ''}
            onChange={(e) => handleFilterChange('paymentMethod', e.target.value || undefined)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Methods</option>
            <option value="cash">Cash</option>
            <option value="debit">Debit</option>
            <option value="online">Online</option>
            <option value="qris">QRIS</option>
          </select>
        </div>

        {/* Start Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={localStartDate}
              onChange={(e) => setLocalStartDate(e.target.value)}
              onBlur={handleDateChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* End Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={localEndDate}
              onChange={(e) => setLocalEndDate(e.target.value)}
              onBlur={handleDateChange}
              min={localStartDate}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Order By Filter */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sort By
        </label>
        <select
          value={filters.orderBy || ''}
          onChange={(e) => handleFilterChange('orderBy', e.target.value || undefined)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Default</option>
          <option value="date">Date (Newest First)</option>
          <option value="date_asc">Date (Oldest First)</option>
          <option value="total_amount">Total Amount (High to Low)</option>
          <option value="total_amount_asc">Total Amount (Low to High)</option>
          <option value="created_at">Created At (Newest First)</option>
          <option value="created_at_asc">Created At (Oldest First)</option>
        </select>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {filters.status && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                Status: {filters.status}
                <button
                  onClick={() => handleFilterRemove('status')}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.paymentMethod && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                Payment: {filters.paymentMethod}
                <button
                  onClick={() => handleFilterRemove('paymentMethod')}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.startDate && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                From: {new Date(filters.startDate).toLocaleDateString()}
                <button
                  onClick={() => handleFilterRemove('startDate')}
                  className="ml-2 text-yellow-600 hover:text-yellow-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.endDate && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                To: {new Date(filters.endDate).toLocaleDateString()}
                <button
                  onClick={() => handleFilterRemove('endDate')}
                  className="ml-2 text-yellow-600 hover:text-yellow-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.orderBy && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                Sort: {filters.orderBy}
                <button
                  onClick={() => handleFilterRemove('orderBy')}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesFilters;
